/**
 * Housing Calculator - Pure Calculation Functions
 *
 * These functions are extracted from HousingCalculator.tsx for:
 * - Easier testing
 * - Code reusability
 * - Cleaner separation of concerns
 */

export interface MortgageInsuranceResult {
  upfrontPremium: number;
  monthlyPremium: number;
}

export interface LandTransferTaxResult {
  provincialTax: number;
  municipalTax: number;
  rebate: number;
  netTax: number;
}

export interface MortgageInterestDeductionResult {
  annualInterest: number[];
  annualDeduction: number[];
  totalDeduction: number;
  totalSavings: number;
  deductibleRatio?: number;
}

/**
 * Calculate monthly mortgage payment using standard amortization formula
 */
export const calculateMonthlyMortgagePayment = (
  principal: number,
  annualRate: number,
  years: number
): number => {
  if (years <= 0) {
    return 0;
  }
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = years * 12;

  if (monthlyRate === 0) return principal / numberOfPayments;

  return principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
         (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
};

/**
 * Calculate mortgage insurance (CMHC for Canada, PMI for USA)
 */
export const calculateMortgageInsurance = (
  loanAmount: number,
  downPaymentPercent: number,
  region: 'Canada' | 'US',
  overrideRate?: number
): MortgageInsuranceResult => {
  // If user provided override, use it
  if (overrideRate !== undefined) {
    if (region === 'Canada') {
      return {
        upfrontPremium: loanAmount * (overrideRate / 100),
        monthlyPremium: 0
      };
    } else {
      return {
        upfrontPremium: 0,
        monthlyPremium: loanAmount * (overrideRate / 100) / 12
      };
    }
  }

  // No insurance needed if 20%+ down payment
  if (downPaymentPercent >= 20) {
    return { upfrontPremium: 0, monthlyPremium: 0 };
  }

  if (region === 'Canada') {
    // CMHC rates (one-time premium added to mortgage)
    let rate = 0;
    if (downPaymentPercent >= 15 && downPaymentPercent < 20) rate = 2.80;
    else if (downPaymentPercent >= 10 && downPaymentPercent < 15) rate = 3.10;
    else if (downPaymentPercent >= 5 && downPaymentPercent < 10) rate = 4.00;
    else rate = 4.50; // < 5%

    return {
      upfrontPremium: loanAmount * (rate / 100),
      monthlyPremium: 0
    };
  } else {
    // PMI for USA (monthly premium until 20% equity)
    const rate = 0.008; // 0.8% annually = typical PMI rate
    return {
      upfrontPremium: 0,
      monthlyPremium: loanAmount * rate / 12
    };
  }
};

/**
 * Calculate Land Transfer Tax and First-Time Buyer Rebates (Canada)
 */
export const calculateLandTransferTax = (
  purchasePrice: number,
  province: string,
  isFirstTime: boolean,
  region: 'Canada' | 'US',
  isToronto: boolean = false
): LandTransferTaxResult => {
  let provincialTax = 0;
  let municipalTax = 0;
  let rebate = 0;

  if (region === 'US') {
    return { provincialTax: 0, municipalTax: 0, rebate: 0, netTax: 0 };
  }

  // Ontario Land Transfer Tax (tiered)
  if (province === 'ON') {
    if (purchasePrice <= 55000) {
      provincialTax = purchasePrice * 0.005;
    } else if (purchasePrice <= 250000) {
      provincialTax = 275 + (purchasePrice - 55000) * 0.01;
    } else if (purchasePrice <= 400000) {
      provincialTax = 2225 + (purchasePrice - 250000) * 0.015;
    } else if (purchasePrice <= 2000000) {
      provincialTax = 4475 + (purchasePrice - 400000) * 0.02;
    } else {
      provincialTax = 36475 + (purchasePrice - 2000000) * 0.025;
    }

    if (isFirstTime) {
      rebate = Math.min(provincialTax, 4000);
    }
  }
  // BC Property Transfer Tax (tiered)
  else if (province === 'BC') {
    if (purchasePrice <= 200000) {
      provincialTax = purchasePrice * 0.01;
    } else if (purchasePrice <= 2000000) {
      provincialTax = 2000 + (purchasePrice - 200000) * 0.02;
    } else if (purchasePrice <= 3000000) {
      provincialTax = 38000 + (purchasePrice - 2000000) * 0.03;
    } else {
      provincialTax = 68000 + (purchasePrice - 3000000) * 0.05;
    }

    // First-time buyers since April 1, 2024: full exemption to $500K, flat $8,000 to $835K, phased out by $860K
    if (isFirstTime) {
      if (purchasePrice <= 500000) {
        rebate = provincialTax;
      } else if (purchasePrice <= 835000) {
        rebate = 8000;
      } else if (purchasePrice < 860000) {
        rebate = 8000 * (860000 - purchasePrice) / 25000;
      }
    }
  }
  // Quebec Welcome Tax (tiered)
  else if (province === 'QC') {
    if (purchasePrice <= 55200) {
      provincialTax = purchasePrice * 0.005;
    } else if (purchasePrice <= 276200) {
      provincialTax = 276 + (purchasePrice - 55200) * 0.01;
    } else if (purchasePrice <= 552300) {
      provincialTax = 2486 + (purchasePrice - 276200) * 0.015;
    } else {
      provincialTax = 6627.50 + (purchasePrice - 552300) * 0.02;
    }
  }
  // Alberta and Saskatchewan have no land transfer tax
  else if (['AB', 'SK'].includes(province)) {
    provincialTax = 0;
  }
  // Manitoba
  else if (province === 'MB') {
    if (purchasePrice <= 30000) {
      provincialTax = 0;
    } else if (purchasePrice <= 90000) {
      provincialTax = (purchasePrice - 30000) * 0.005;
    } else if (purchasePrice <= 150000) {
      provincialTax = 300 + (purchasePrice - 90000) * 0.01;
    } else if (purchasePrice <= 200000) {
      provincialTax = 900 + (purchasePrice - 150000) * 0.015;
    } else {
      provincialTax = 1650 + (purchasePrice - 200000) * 0.02;
    }
  }
  // Default for other provinces
  else {
    provincialTax = purchasePrice * 0.01;
  }

  // Toronto Municipal Land Transfer Tax
  if (isToronto) {
    if (purchasePrice <= 55000) {
      municipalTax = purchasePrice * 0.005;
    } else if (purchasePrice <= 250000) {
      municipalTax = 275 + (purchasePrice - 55000) * 0.01;
    } else if (purchasePrice <= 400000) {
      municipalTax = 2225 + (purchasePrice - 250000) * 0.015;
    } else if (purchasePrice <= 2000000) {
      municipalTax = 4475 + (purchasePrice - 400000) * 0.02;
    } else {
      municipalTax = 36475 + (purchasePrice - 2000000) * 0.025;
    }

    if (isFirstTime) {
      rebate += Math.min(municipalTax, 4475);
    }
  }

  const netTax = Math.max(0, provincialTax + municipalTax - rebate);

  return { provincialTax, municipalTax, rebate, netTax };
};

/**
 * US Federal Tax Brackets for 2026 (Single filers, Rev. Proc. 2025-32)
 */
export const US_TAX_BRACKETS = [
  { min: 0, max: 12400, rate: 10 },
  { min: 12400, max: 50400, rate: 12 },
  { min: 50400, max: 105700, rate: 22 },
  { min: 105700, max: 201775, rate: 24 },
  { min: 201775, max: 256225, rate: 32 },
  { min: 256225, max: 640600, rate: 35 },
  { min: 640600, max: Infinity, rate: 37 }
];

/** SALT deduction cap: $40,000 for 2025-2029 under P.L. 119-21 (rising 1%/yr), back to $10,000 in 2030. */
export const US_SALT_CAP = 40000;

/**
 * Calculate US Mortgage Interest Deduction
 * Note: This is a simplified calculation - actual tax benefits depend on whether
 * itemizing is better than standard deduction
 */
export const calculateMortgageInterestDeduction = (
  annualInterestPaid: number[],
  annualPropertyTax: number[],
  loanAmount: number,
  marginalTaxRate: number
): MortgageInterestDeductionResult => {
  // US mortgage interest deduction caps (2018 TCJA):
  // - Mortgage debt limit: $750,000 for new loans
  // - Property tax deduction (SALT): capped at US_SALT_CAP
  const mortgageDebtLimit = 750000;
  const deductibleRatio = Math.min(1, mortgageDebtLimit / loanAmount);

  const annualDeduction: number[] = [];
  let totalDeduction = 0;

  for (let i = 0; i < annualInterestPaid.length; i++) {
    const yearInterest = annualInterestPaid[i];
    const yearPropertyTax = annualPropertyTax[i];

    // Apply mortgage debt limit ratio to interest
    const deductibleInterest = yearInterest * deductibleRatio;

    // Property tax deduction capped at the SALT cap
    const deductiblePropertyTax = Math.min(yearPropertyTax, US_SALT_CAP);

    // Total itemized deduction for this year
    const yearDeduction = deductibleInterest + deductiblePropertyTax;

    annualDeduction.push(yearDeduction);
    totalDeduction += yearDeduction;
  }

  // Tax savings = deduction * marginal tax rate
  const totalSavings = totalDeduction * (marginalTaxRate / 100);

  return {
    annualInterest: annualInterestPaid,
    annualDeduction,
    totalDeduction,
    totalSavings,
    deductibleRatio
  };
};

/**
 * Calculate break-even year where buying becomes cheaper than renting
 * Returns -1 if buying never becomes cheaper within the period
 */
export const calculateBreakEvenYear = (
  cumulativeBuyCosts: number[],
  cumulativeRentCosts: number[],
  homeEquity: number[]
): number => {
  for (let i = 0; i < cumulativeBuyCosts.length; i++) {
    const netBuyCost = cumulativeBuyCosts[i] - homeEquity[i];
    if (netBuyCost <= cumulativeRentCosts[i]) {
      return i + 1; // Year is 1-indexed
    }
  }
  return -1; // Never breaks even
};


/**
 * Tax rates used by the housing calculator.
 * Canada includes half of a capital gain in taxable income (the 2024 proposal to raise this to
 * two-thirds was cancelled in 2025). Net rental income is fully taxable at the marginal rate.
 */
export const CAPITAL_GAINS_INCLUSION_RATE = 0.5;

/** US path keeps the flat rate the calculator has always used; long-term gains there run 15-20% federal plus state. */
export const US_CAPITAL_GAINS_TAX_RATE = 25;

/** Canada: combined federal + Ontario rate around $110K-$150K. US: the 22% federal bracket, typical for a dual-income buyer. */
export const defaultMarginalTaxRate = (region: 'Canada' | 'US'): number => (region === 'Canada' ? 40 : 22);

/** Effective tax on a capital gain (home sale or taxable investment account), as a percent. */
export const capitalGainsTaxRate = (marginalTaxRate: number, region: 'Canada' | 'US'): number =>
  region === 'Canada' ? marginalTaxRate * CAPITAL_GAINS_INCLUSION_RATE : US_CAPITAL_GAINS_TAX_RATE;

/** 2026 US standard deduction, single filer (Rev. Proc. 2025-32). Itemized deductions only save tax above this. */
export const US_STANDARD_DEDUCTION_SINGLE = 16100;
